/**
 * Post-submit contract between SecureCredentialModal and its callers.
 *
 * The modal owns the outcome of a save: callers perform the network call
 * and resolve with a result, and the modal renders either the in-sheet
 * success screen (caller is told via `onClose` when the user dismisses
 * it) or an inline failure. Callers must NOT unmount the modal on success
 * — they only unmount on `onClose`.
 *
 * Dependency-free (no React / axios imports) so the error copy can be
 * unit-tested in isolation.
 */

export interface SecureCredentialSubmitError {
  /** Structured BE code, e.g. NO_SECRET_FIELD. */
  code?: string;
  /** HTTP status when the failure came from a response. */
  status?: number;
  /** Server-supplied or transport message. */
  message?: string;
  /** VALUE_BLEED_INTO_METADATA: which metadata field the secret leaked into. */
  which?: 'label' | 'purpose';
  /** VALUE_BLEED_INTO_METADATA: the credential field whose value leaked. */
  fieldKey?: string;
  /** DUPLICATE_CREDENTIAL: the existing rows that collided. */
  candidates?: Array<{ label: string; purpose?: string | null }>;
}

export type SecureCredentialSubmitResult =
  | { ok: true }
  | { ok: false; error: SecureCredentialSubmitError };

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      error?: unknown;
      message?: unknown;
      code?: unknown;
      which?: unknown;
      fieldKey?: unknown;
      candidates?: unknown;
    };
  };
  message?: unknown;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

/**
 * Normalise whatever a caller caught (axios error, plain Error, string)
 * into the structured shape the modal renders from.
 */
export function submitErrorFromUnknown(
  err: unknown,
): SecureCredentialSubmitError {
  if (!isRecord(err)) {
    return { message: typeof err === 'string' ? err : undefined };
  }
  const e = err as AxiosLikeError;
  const data = e.response?.data;
  const out: SecureCredentialSubmitError = {};
  if (typeof e.response?.status === 'number') out.status = e.response.status;
  if (data) {
    if (typeof data.code === 'string') out.code = data.code;
    if (typeof data.error === 'string') out.message = data.error;
    else if (typeof data.message === 'string') out.message = data.message;
    if (data.which === 'label' || data.which === 'purpose')
      out.which = data.which;
    if (typeof data.fieldKey === 'string') out.fieldKey = data.fieldKey;
    if (Array.isArray(data.candidates)) {
      out.candidates = data.candidates.filter(isRecord).map((c) => ({
        label: typeof c.label === 'string' ? c.label : 'Saved sign-in',
        purpose: typeof c.purpose === 'string' ? c.purpose : null,
      }));
    }
  }
  if (!out.message && typeof e.message === 'string') out.message = e.message;
  return out;
}

export interface SubmitErrorCopy {
  title: string;
  body: string;
}

const humanFieldName = (key: string | undefined): string =>
  (key || 'secret').replace(/[_-]+/g, ' ');

/**
 * Map a structured failure to the two lines the sheet renders. Copy is
 * load-bearing: category before brand ("Bitwarden, the password
 * manager"), no claims about who at Dream can or can't see a value, no
 * promises the product doesn't keep.
 */
export function describeSubmitError(
  error: SecureCredentialSubmitError,
  opts: { isUpdate?: boolean } = {},
): SubmitErrorCopy {
  const verb = opts.isUpdate ? 'updated' : 'saved';
  switch (error.code) {
    case 'NO_SECRET_FIELD':
      return {
        title: 'Nothing here is a secret.',
        body: `Nothing was ${verb}. Add a password, PIN or code — a sign-in needs at least one.`,
      };
    case 'NO_IDENTIFIER_FIELD':
      return {
        title: 'Whose account is this?',
        body: `Nothing was ${verb}. Add a username, email or account name alongside the secret.`,
      };
    case 'VALUE_BLEED_INTO_METADATA': {
      const which = error.which === 'purpose' ? 'purpose' : 'label';
      return {
        title: `Your ${humanFieldName(error.fieldKey)} also appears in the ${which}.`,
        body: `Nothing was ${verb}. The ${which} stays in Dream, not in the vault, so it can't contain the secret. Change one of them and try again.`,
      };
    }
    case 'DUPLICATE_CREDENTIAL': {
      const names = (error.candidates ?? [])
        .map((c) => (c.purpose ? `${c.label} (${c.purpose})` : c.label))
        .join(', ');
      return {
        title: 'You already have a sign-in for this service.',
        body: `Nothing was ${verb}.${names ? ` Existing: ${names}.` : ''} Edit the existing one from Memories instead.`,
      };
    }
    default:
      break;
  }
  if (error.status === 503) {
    return {
      title: 'The vault isn\u2019t set up yet.',
      body: `Nothing was ${verb}. Bitwarden, the password manager, isn\u2019t configured for this account — contact support and we\u2019ll sort it.`,
    };
  }
  if (error.status === 409) {
    return {
      title: 'Already handled.',
      body: 'This request has been answered already, so nothing more is needed here.',
    };
  }
  if (error.status === 410) {
    return {
      title: 'This link has expired.',
      body: `Nothing was ${verb}. Ask your secretary for a fresh link.`,
    };
  }
  return {
    title: opts.isUpdate
      ? 'Couldn\u2019t update it.'
      : 'Couldn\u2019t save it.',
    body: error.message
      ? `Nothing was ${verb}. ${error.message}`
      : `Nothing was ${verb}. Check your connection and try again.`,
  };
}
